import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  TableChart,
  Dashboard as DashboardIcon,
  CalendarToday,
  Policy,
  Gavel,
  EventAvailable,
  EventNote,
  Publish,
  AccessTime,
  PictureAsPdf,
  School
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { roleMatches } from '../../utils/rolePermissions';
import WorkflowGuide from '../common/WorkflowGuide';
import CompanyPolicyManagement from './CompanyPolicyManagement';
import OpsAvailabilityAndLeave from './OpsAvailabilityAndLeave';
import DgcaBaselineView from './DgcaBaselineView';

// Import hooks
import { useCrewDutyRosterData } from './CrewDutyRoster/hooks/useCrewDutyRosterData';
import { useCrewDutyRosterHandlers } from './CrewDutyRoster/hooks/useCrewDutyRosterHandlers';
// Import components
import CrewDutyRosterFilters, {
  collectCrewBaseCities,
  entryMatchesBaseCity
} from './CrewDutyRoster/components/CrewDutyRosterFilters';
import CrewDutyRosterTable from './CrewDutyRoster/components/CrewDutyRosterTable';
import CrewDutyRosterCalendar from './CrewDutyRoster/components/CrewDutyRosterCalendar';
import CrewDutyRosterDashboard from './CrewDutyRoster/components/CrewDutyRosterDashboard';
import RosteringPlanPanel from './CrewDutyRoster/components/RosteringPlanPanel';
import PublishedRostersPanel from './CrewDutyRoster/components/PublishedRostersPanel';
import CrewHoursBoard from './CrewDutyRoster/components/CrewHoursBoard';
import WeeklyRestSuggestionsPanel from './CrewDutyRoster/components/WeeklyRestSuggestionsPanel';
import CrewTrainingUpdatesPanel from './CrewDutyRoster/components/CrewTrainingUpdatesPanel';
// Import dialogs
import CrewDutyRosterDialog from './CrewDutyRoster/dialogs/CrewDutyRosterDialog';
import CrewDutyRosterOverrideDialog from './CrewDutyRoster/dialogs/CrewDutyRosterOverrideDialog';
import PublishRosterDialog from './CrewDutyRoster/dialogs/PublishRosterDialog';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CrewDutyRoster = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [clientSettings, setClientSettings] = useState({
    displayTimezone: 'Asia/Kolkata',
    rosterPlanningDefaults: { plannedWorkDays: 20, plannedOffDays: 10 }
  });
  const [filters, setFilters] = useState({
    crewId: '',
    baseCity: '',
    startDate: '',
    endDate: '',
    dutyType: '',
    status: ''
  });

  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishedRefreshKey, setPublishedRefreshKey] = useState(0);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setClientSettings((s) => ({
          ...s,
          displayTimezone: res.data?.displayTimezone || s.displayTimezone,
          rosterPlanningDefaults: res.data?.rosterPlanningDefaults || s.rosterPlanningDefaults
        }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const defaultPlannedOffDays = clientSettings.rosterPlanningDefaults?.plannedOffDays ?? 10;

  const isOpsRoster = roleMatches(user?.role, [
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);
  const isCrewSelfView = roleMatches(user?.role, [ROLES.CREW]);

  // Crew always see only their own roster entries
  useEffect(() => {
    if (!isCrewSelfView || !user?._id) return;
    setFilters((prev) => (prev.crewId === user._id ? prev : { ...prev, crewId: user._id }));
  }, [isCrewSelfView, user?._id]);

  // Calendar view state
  const [calendarStartDate, setCalendarStartDate] = useState(dayjs().startOf('week'));
  const [calendarEndDate, setCalendarEndDate] = useState(dayjs().endOf('week'));
  const [calendarViewType, setCalendarViewType] = useState('week'); // 'month', 'week', 'day'
  const [calendarDisplayMode, setCalendarDisplayMode] = useState('grid'); // 'grid' or 'calendar'
  const [selectedCrewFilter, setSelectedCrewFilter] = useState('');

  // Use data fetching hook
  const {
    entries,
    setEntries,
    crewMembers,
    availableCrew,
    aircraftList,
    loading,
    dashboardData,
    calendarAvailability,
    calendarLoading,
    fetchEntries
  } = useCrewDutyRosterData(filters, tabValue, calendarStartDate, calendarEndDate);

  // Use handlers hook
  const {
    dialogOpen,
    setDialogOpen,
    selectedEntry,
    formData,
    setFormData,
    aircraftType,
    setAircraftType,
    complianceWarnings,
    complianceViolations,
    complianceContext,
    partIiiEligible,
    requiredCompensatoryRestHours,
    hardBlockMessages,
    overrideDialogOpen,
    setOverrideDialogOpen,
    handleCreate,
    handleCreateFromMismatch,
    handleEdit,
    handleViewDutyFromCalendar,
    handleSave,
    handleOverride,
    handleDelete,
    handleApplyTechlogProposed
  } = useCrewDutyRosterHandlers(fetchEntries, setEntries, displayTimezone);

  const canEdit = (entry) => {
    return [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  };

  const canDelete = (entry) => {
    return [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  };

  const canDeletePlanning = [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(
    user?.role
  );

  const baseCityOptions = collectCrewBaseCities(crewMembers);

  const filteredEntries = (entries || []).filter((entry) =>
    entryMatchesBaseCity(entry, filters.baseCity, null)
  );

  const showPublishActions = canEdit() && (tabValue < 3 || tabValue === 4 || tabValue === 8);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{isCrewSelfView ? 'My Duty Roster' : 'Crew Duty'}</Typography>
          {showPublishActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isOpsRoster && (
                <Button
                  variant="outlined"
                  startIcon={<Publish />}
                  onClick={() => setPublishDialogOpen(true)}
                >
                  Publish Roster
                </Button>
              )}
              {tabValue < 3 && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreate}
                >
                  Create Duty Entry
                </Button>
              )}
            </Box>
          )}
        </Box>

        {!isCrewSelfView && <WorkflowGuide variant="crewDutyRoster" />}

        {isOpsRoster && (
          <WeeklyRestSuggestionsPanel onAccepted={fetchEntries} />
        )}

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3, maxWidth: '100%' }}>
          <Tab icon={<TableChart />} label={isCrewSelfView ? 'My Roster' : 'Table View'} />
          {isOpsRoster && <Tab icon={<CalendarToday />} label="Calendar View" />}
          {isOpsRoster && <Tab icon={<DashboardIcon />} label="Dashboard" />}
          {isOpsRoster && <Tab icon={<AccessTime />} label="Crew Hours" />}
          {isCrewSelfView && <Tab icon={<AccessTime />} label="My Hours" />}
          {isOpsRoster && <Tab icon={<EventNote />} label="Rostering" />}
          {isOpsRoster && <Tab icon={<EventAvailable />} label="Availability & Leave" />}
          {isOpsRoster && <Tab icon={<Policy />} label="Company Policies" />}
          {isOpsRoster && <Tab icon={<Gavel />} label="DGCA Baseline" />}
          {(isOpsRoster || isCrewSelfView) && <Tab icon={<PictureAsPdf />} label="Published" />}
          {(isOpsRoster || isCrewSelfView) && <Tab icon={<School />} label={isCrewSelfView ? 'My Certs & Trainings' : 'Certs & Trainings'} />}
        </Tabs>

        {/* Filters — crew self-view keeps date/status filters only */}
        <CrewDutyRosterFilters
          filters={filters}
          setFilters={setFilters}
          crewMembers={isCrewSelfView ? [] : crewMembers}
          tabValue={tabValue}
          displayTimezone={displayTimezone}
          hideCrewFilter={isCrewSelfView}
        />

        {/* Table View */}
        {tabValue === 0 && (
          <CrewDutyRosterTable
            entries={filteredEntries}
            loading={loading}
            availableCrew={availableCrew}
            crewMembers={crewMembers}
            filters={filters}
            canEdit={canEdit}
            canDelete={canDelete}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleApplyTechlogProposed={handleApplyTechlogProposed}
            displayTimezone={displayTimezone}
          />
        )}

        {/* Calendar View (ops) */}
        {isOpsRoster && tabValue === 1 && (
          <CrewDutyRosterCalendar
            calendarAvailability={calendarAvailability}
            calendarLoading={calendarLoading}
            calendarStartDate={calendarStartDate}
            setCalendarStartDate={setCalendarStartDate}
            calendarEndDate={calendarEndDate}
            setCalendarEndDate={setCalendarEndDate}
            calendarViewType={calendarViewType}
            setCalendarViewType={setCalendarViewType}
            calendarDisplayMode={calendarDisplayMode}
            setCalendarDisplayMode={setCalendarDisplayMode}
            selectedCrewFilter={selectedCrewFilter}
            setSelectedCrewFilter={setSelectedCrewFilter}
            crewMembers={crewMembers.filter((c) =>
              !filters.baseCity
                || String(c?.workLocation?.city || '').trim().toLowerCase()
                  === String(filters.baseCity).trim().toLowerCase()
            )}
            handleViewDutyFromCalendar={handleViewDutyFromCalendar}
            displayTimezone={displayTimezone}
          />
        )}

        {/* Dashboard View (ops) */}
        {isOpsRoster && tabValue === 2 && (
          <CrewDutyRosterDashboard dashboardData={dashboardData} />
        )}

        {/* Crew Hours / FDTL board (ops) */}
        {isOpsRoster && tabValue === 3 && (
          <CrewHoursBoard
            crewMembers={crewMembers}
            displayTimezone={displayTimezone}
            onFixMismatch={(item) => {
              setTabValue(0);
              handleCreateFromMismatch(item);
            }}
          />
        )}

        {/* My Hours (crew self) — second tab when not ops */}
        {isCrewSelfView && tabValue === 1 && (
          <CrewHoursBoard
            selfCrewId={user._id}
            displayTimezone={displayTimezone}
          />
        )}

        {/* Rostering (ops planning calendar) */}
        {isOpsRoster && tabValue === 4 && (
          <>
            <PublishedRostersPanel
              baseOptions={baseCityOptions}
              selectedBaseCity={filters.baseCity || ''}
              onBaseCityChange={(baseCity) => setFilters((f) => ({ ...f, baseCity }))}
              refreshKey={publishedRefreshKey}
              compact
            />
            <RosteringPlanPanel
              crewMembers={crewMembers}
              displayTimezone={displayTimezone}
              filters={filters}
              defaultPlannedOffDays={defaultPlannedOffDays}
              onViewDuty={handleViewDutyFromCalendar}
              onRefreshParent={fetchEntries}
              canDeletePlanning={canDeletePlanning}
            />
          </>
        )}

        {/* Availability Management (Ops) */}
        {isOpsRoster && tabValue === 5 && <OpsAvailabilityAndLeave />}

        {/* Company Policy Management */}
        {isOpsRoster && tabValue === 6 && <CompanyPolicyManagement />}

        {/* DGCA Baseline View (Read-Only) */}
        {isOpsRoster && tabValue === 7 && <DgcaBaselineView />}

        {/* Published rosters — view / download (ops tab 8, crew tab 2) */}
        {((isOpsRoster && tabValue === 8) || (isCrewSelfView && tabValue === 2)) && (
          <PublishedRostersPanel
            baseOptions={baseCityOptions}
            selectedBaseCity={filters.baseCity || ''}
            onBaseCityChange={(baseCity) => setFilters((f) => ({ ...f, baseCity }))}
            refreshKey={publishedRefreshKey}
          />
        )}

        {/* Crew Training Updates (ops tab 9, crew tab 3) */}
        {((isOpsRoster && tabValue === 9) || (isCrewSelfView && tabValue === 3)) && (
          <CrewTrainingUpdatesPanel
            crewMembers={crewMembers}
            selfCrewId={isCrewSelfView ? user._id : null}
          />
        )}

        {/* Create/Edit Dialog */}
        <CrewDutyRosterDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          selectedEntry={selectedEntry}
          formData={formData}
          setFormData={setFormData}
          aircraftType={aircraftType}
          setAircraftType={setAircraftType}
          crewMembers={crewMembers}
          aircraftList={aircraftList}
          complianceContext={complianceContext}
          complianceViolations={complianceViolations}
          complianceWarnings={complianceWarnings}
          handleSave={handleSave}
          displayTimezone={displayTimezone}
        />

        {/* Override Confirmation Dialog */}
        <CrewDutyRosterOverrideDialog
          overrideDialogOpen={overrideDialogOpen}
          setOverrideDialogOpen={setOverrideDialogOpen}
          formData={formData}
          setFormData={setFormData}
          aircraftType={aircraftType}
          complianceContext={complianceContext}
          complianceViolations={complianceViolations}
          complianceWarnings={complianceWarnings}
          partIiiEligible={partIiiEligible}
          requiredCompensatoryRestHours={requiredCompensatoryRestHours}
          hardBlockMessages={hardBlockMessages}
          handleOverride={handleOverride}
        />

        <PublishRosterDialog
          open={publishDialogOpen}
          onClose={(published) => {
            setPublishDialogOpen(false);
            if (published) setPublishedRefreshKey((k) => k + 1);
          }}
          displayTimezone={displayTimezone}
          baseOptions={baseCityOptions}
          initialBaseCity={filters.baseCity || ''}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CrewDutyRoster;
